import * as XLSX from "xlsx";
import { collection, getDocs, writeBatch, doc } from "firebase/firestore";
import { db } from "../lib/firebase";
import { COLLECTIONS, generateDocNumber } from "./firestore";

/* =====================================================
   COMMON HELPER
===================================================== */

const readExcelFile = async (file) => {

  const data = await file.arrayBuffer();
  const workbook = XLSX.read(data);
  const sheet = workbook.Sheets[workbook.SheetNames[0]];

  return XLSX.utils.sheet_to_json(sheet);

};

/* =====================================================
   ITEM EXPORT
===================================================== */

export const exportItemsToExcel = async () => {

  const snapshot = await getDocs(collection(db, COLLECTIONS.ITEMS));

  const rows = snapshot.docs.map(d => {

    const data = d.data();

    return {
      item_id: data.item_id || "",
      item_code: data.item_code || "",
      item_name: data.item_name || "",
      description: data.description || "",
      HSN: data.HSN || "",
      UOM: data.UOM || "",
      GST_percent: data.GST_percent || 0,
      rate: data.rate || 0,
      brand: data.brand || "",
      category: data.category || ""
    };

  });

  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(wb, ws, "Items");

  XLSX.writeFile(wb, "sunstore_items.xlsx");

};

/* =====================================================
   ITEMS TEMPLATE
===================================================== */

export const downloadItemsTemplate = () => {

  const template = [{
    item_id: "",
    item_code: "",
    item_name: "",
    description: "",
    HSN: "",
    UOM: "",
    GST_percent: "",
    rate: "",
    brand: "",
    category: ""
  }];

  const ws = XLSX.utils.json_to_sheet(template);
  const wb = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(wb, ws, "Items Template");

  XLSX.writeFile(wb, "sunstore_items_template.xlsx");

};

/* =====================================================
   ITEMS PREVIEW ANALYSIS
===================================================== */

export const analyzeItemsExcel = async (file) => {

  const rows = await readExcelFile(file);

  const snapshot = await getDocs(collection(db, COLLECTIONS.ITEMS));

  const existingMap = {};

  snapshot.docs.forEach(d => {
    const data = d.data();
    if (data.item_id) {
      existingMap[data.item_id] = d;
    }
  });

  let updateCount = 0;
  let createCount = 0;

  rows.forEach(row => {

    if (row.item_id && existingMap[row.item_id]) {
      updateCount++;
    } else {
      createCount++;
    }

  });

  return {
    rows,
    total: rows.length,
    updateCount,
    createCount
  };

};

/* =====================================================
   IMPORT ITEMS
===================================================== */

export const importItemsFromExcel = async (rows) => {

  const snapshot = await getDocs(collection(db, COLLECTIONS.ITEMS));

  const existingMap = {};

  snapshot.docs.forEach(d => {
    const data = d.data();
    if (data.item_id) {
      existingMap[data.item_id] = d;
    }
  });

  let batch = writeBatch(db);
  let count = 0;

  for (const row of rows) {

    const itemId = row.item_id;

    const itemData = {

      item_code: row.item_code || "",
      item_name: row.item_name || "",
      description: row.description || "",
      HSN: String(row.HSN || ""),
      UOM: row.UOM || "",
      GST_percent: Number(row.GST_percent || 0),
      rate: Number(row.rate || 0),
      brand: row.brand || "",
      category: row.category || ""

    };

    if (itemId && existingMap[itemId]) {

      const ref = existingMap[itemId].ref;

      batch.update(ref, itemData);

    } else {

      const newId = await generateDocNumber("item");

      const ref = doc(collection(db, COLLECTIONS.ITEMS));

      batch.set(ref, {
        ...itemData,
        item_id: newId
      });

    }

    count++;

    if (count === 500) {

      await batch.commit();
      batch = writeBatch(db);
      count = 0;

    }

  }

  if (count > 0) {
    await batch.commit();
  }

};

/* =====================================================
   PARTY EXPORT
===================================================== */

export const exportPartiesToExcel = async () => {

  const snapshot = await getDocs(collection(db, COLLECTIONS.PARTIES));

  const rows = snapshot.docs.map(d => {

    const data = d.data();

    return {
      party_id: data.party_id || "",
      party_name: data.party_name || "",
      address: data.address || "",
      city: data.city || "",
      state: data.state || "",
      GST_number: data.GST_number || "",
      phone: data.phone || data.mobile || "",
      email: data.email || ""
    };

  });

  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(wb, ws, "Parties");

  XLSX.writeFile(wb, "sunstore_parties.xlsx");

};

/* =====================================================
   PARTY TEMPLATE
===================================================== */

export const downloadPartiesTemplate = () => {

  const template = [{
    party_id: "",
    party_name: "",
    address: "",
    city: "",
    state: "",
    GST_number: "",
    phone: "",
    email: ""
  }];

  const ws = XLSX.utils.json_to_sheet(template);
  const wb = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(wb, ws, "Parties Template");

  XLSX.writeFile(wb, "sunstore_parties_template.xlsx");

};

/* =====================================================
   PARTIES PREVIEW ANALYSIS
===================================================== */

export const analyzePartiesExcel = async (file) => {

  const rows = await readExcelFile(file);

  const snapshot = await getDocs(collection(db, COLLECTIONS.PARTIES));

  const existingMap = {};

  snapshot.docs.forEach(d => {
    const data = d.data();
    if (data.party_id) {
      existingMap[data.party_id] = d;
    }
  });

  let updateCount = 0;
  let createCount = 0;

  rows.forEach(row => {

    if (row.party_id && existingMap[row.party_id]) {
      updateCount++;
    } else {
      createCount++;
    }

  });

  return {
    rows,
    total: rows.length,
    updateCount,
    createCount
  };

};

/* =====================================================
   IMPORT PARTIES
===================================================== */

export const importPartiesFromExcel = async (rows) => {

  const snapshot = await getDocs(collection(db, COLLECTIONS.PARTIES));

  const existingMap = {};

  snapshot.docs.forEach(d => {
    const data = d.data();
    if (data.party_id) {
      existingMap[data.party_id] = d;
    }
  });

  let batch = writeBatch(db);
  let count = 0;

  for (const row of rows) {

    const partyId = row.party_id;

    const partyData = {

      party_name: row.party_name || "",
      address: row.address || "",
      city: row.city || "",
      state: row.state || "",
      GST_number: row.GST_number || "",
      phone: row.phone || row.mobile || "",
      email: row.email || ""

    };

    if (partyId && existingMap[partyId]) {

      const ref = existingMap[partyId].ref;

      batch.update(ref, partyData);

    } else {

      const newId = await generateDocNumber("party");

      const ref = doc(collection(db, COLLECTIONS.PARTIES));

      batch.set(ref, {
        ...partyData,
        party_id: newId
      });

    }

    count++;

    if (count === 500) {

      await batch.commit();
      batch = writeBatch(db);
      count = 0;

    }

  }

  if (count > 0) {
    await batch.commit();
  }

};